import { HTTPError } from 'ky'

interface ApiErrorPayload {
  error?: string
  errorCode?: string
  message?: string
  limit?: string
  allowed?: number | null
}

interface LimitNotification {
  title: string
  description: string
}

async function getApiErrorPayload(
  error: unknown,
): Promise<ApiErrorPayload | null> {
  if (!(error instanceof HTTPError)) {
    return null
  }

  try {
    return (await error.response.clone().json()) as ApiErrorPayload
  } catch {
    return null
  }
}

export async function getApiErrorCode(error: unknown): Promise<string | null> {
  const payload = await getApiErrorPayload(error)
  return payload?.errorCode ?? payload?.error ?? payload?.message ?? null
}

function formatAllowedCount(
  allowed: number | null,
  singular: string,
  plural: string,
  scope: string,
): string {
  if (allowed === null) {
    return `Your current plan allows unlimited ${plural}${scope}.`
  }

  return allowed === 1
    ? `Your current plan allows 1 ${singular}${scope}.`
    : `Your current plan allows ${allowed} ${plural}${scope}.`
}

export async function getPlanLimitNotification(
  error: unknown,
): Promise<LimitNotification | null> {
  const payload = await getApiErrorPayload(error)
  if (
    payload?.error !== 'PLAN_LIMIT_EXCEEDED' ||
    typeof payload.limit !== 'string'
  ) {
    return null
  }

  const allowed = typeof payload.allowed === 'number' ? payload.allowed : null

  switch (payload.limit) {
    case 'workspaces':
      return {
        title: 'Workspace limit reached',
        description: formatAllowedCount(allowed, 'workspace', 'workspaces', ''),
      }
    case 'plugins':
      return {
        title: 'Plugin limit reached',
        description: formatAllowedCount(allowed, 'plugin', 'plugins', ''),
      }
    case 'seats':
      return {
        title: 'Seat limit reached',
        description: formatAllowedCount(allowed, 'seat', 'seats', ''),
      }
    case 'threadsPerWorkspace':
      return {
        title: 'Chat limit reached',
        description: formatAllowedCount(
          allowed,
          'chat',
          'chats',
          ' in this workspace',
        ),
      }
    case 'messagesPerThread':
      return {
        title: 'Message limit reached',
        description: formatAllowedCount(
          allowed,
          'message',
          'messages',
          ' in this chat',
        ),
      }
    case 'messagesPerWorkspaceMonth':
      return {
        title: 'Monthly message limit reached',
        description:
          allowed === null
            ? 'Your workspace has reached its monthly message limit. Upgrade to a paid plan to continue chatting.'
            : `Your workspace has used all ${allowed} messages included in the Free plan this month. Upgrade to a paid plan for unlimited messages.`,
      }
    default:
      return {
        title: 'Plan limit reached',
        description:
          payload.message ?? 'Your current plan does not allow this action.',
      }
  }
}

export function isWorkspaceAccessErrorCode(code: string | null): boolean {
  return (
    code === 'FORBIDDEN' ||
    code === 'FORBIDDEN_NOT_WORKSPACE_MEMBER' ||
    code === 'CHAT_NOT_FOUND'
  )
}
